import { denormalisedResponseEntities } from './data';
import { getStartOf, stringifyDateToISO8601 } from './dates';

const getSlotInterval = timeSlot => {
  const { start, end, seats = 1 } = timeSlot?.attributes || {};
  return start instanceof Date && end instanceof Date
    ? { start: start.getTime(), end: end.getTime(), seats }
    : null;
};

export const queryListingTimeSlots = (sdk, params) =>
  sdk.timeslots.query(params).then(response => denormalisedResponseEntities(response));

export const queryAllListingTimeSlots = async (sdk, params) => {
  const perPage = Math.min(params.perPage || 500, 500);
  const firstPage = params.page || 1;
  let page = firstPage;
  let totalPages = firstPage;
  const timeSlots = [];

  do {
    const response = await sdk.timeslots.query({ ...params, perPage, page });
    timeSlots.push(...denormalisedResponseEntities(response));
    totalPages = response?.data?.meta?.totalPages || page;
    page += 1;
  } while (page <= totalPages);

  return timeSlots;
};

export const isBookingIntervalAvailable = (
  bookingStart,
  bookingEnd,
  timeSlots,
  minimumSeats = 1
) => {
  const start = bookingStart instanceof Date ? bookingStart.getTime() : NaN;
  const end = bookingEnd instanceof Date ? bookingEnd.getTime() : NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return false;
  }

  const availableIntervals = (timeSlots || [])
    .map(getSlotInterval)
    .filter(slot => slot && slot.seats >= minimumSeats && slot.end > start && slot.start < end)
    .sort((a, b) => a.start - b.start);

  let coveredUntil = start;
  for (const slot of availableIntervals) {
    if (slot.start > coveredUntil) {
      return false;
    }
    coveredUntil = Math.max(coveredUntil, slot.end);
    if (coveredUntil >= end) {
      return true;
    }
  }

  return false;
};

export const canStartBookingAt = (
  bookingStart,
  timeSlots,
  minimumDurationMinutes = 30,
  minimumSeats = 1
) => {
  if (!(bookingStart instanceof Date) || Number.isNaN(bookingStart.getTime())) {
    return false;
  }
  const bookingEnd = new Date(bookingStart.getTime() + minimumDurationMinutes * 60 * 1000);
  return isBookingIntervalAvailable(bookingStart, bookingEnd, timeSlots, minimumSeats);
};

export const getAvailabilityDateMap = (
  rangeStart,
  rangeEnd,
  timeSlots,
  timeZone,
  minimumDurationMinutes = 30
) => {
  const availability = {};
  let day = getStartOf(rangeStart, 'day', timeZone);
  const exclusiveEnd = getStartOf(rangeEnd, 'day', timeZone);

  while (day < exclusiveEnd) {
    const nextDay = getStartOf(day, 'day', timeZone, 1, 'days');
    const hasAvailability = (timeSlots || []).some(timeSlot => {
      const slot = getSlotInterval(timeSlot);
      if (!slot || slot.seats < 1) return false;
      const overlapStart = Math.max(day.getTime(), slot.start);
      const overlapEnd = Math.min(nextDay.getTime(), slot.end);
      return overlapEnd - overlapStart >= minimumDurationMinutes * 60 * 1000;
    });
    availability[stringifyDateToISO8601(day, timeZone)] = hasAvailability;
    day = nextDay;
  }

  return availability;
};
